import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { AlertCircle, Trash2 } from "lucide-react";
import { PresetBasicInfo } from "./PresetBasicInfo";
import { PresetGallery } from "./PresetGallery";
import { FormFooter } from "@/shared/widgets/form-actions";
import { SimpleButton } from "@/shared/widgets/feedback";
import { usePresetForm } from "./usePresetForm";
import { LutPreviewDialog } from "./LutPreviewDialog";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { toast } from "@/shared/hooks/use-toast";
import { DeletePresetModal } from "./DeletePresetModal";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import PresetService from "@/features/packs/services/presets/presetService";
import { useTranslation } from "@/shared/i18n";

export default function PresetForm() {
  const { t } = useTranslation('presets');
  const location = useLocation();
  const { id: presetId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Get pack info from location state
  const packId = location.state?.packId;
  const packName = location.state?.packName;
  const isEditing = Boolean(presetId);
  
  // Buscar dados do preset da API quando estiver editando
  const { data: presetData, isLoading: isLoadingPreset } = useCustomQuery({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.getPresetById,
    parameter: { id: presetId! },
    options: { enabled: isEditing && !!presetId },
  }); 
  const [isLutPreviewOpen, setLutPreviewOpen] = useState(false);
  const [lutPreviewUrl, setLutPreviewUrl] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Estado local para presetsTags (para atualizações em tempo real)
  const [localPresetsTags, setLocalPresetsTags] = useState(presetData?.presetsTags || []);
  
  // Atualizar presetsTags quando presetData for carregado
  useEffect(() => {
    if (presetData?.presetsTags) {
      setLocalPresetsTags(presetData.presetsTags);
    }
  }, [presetData]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const presetForm = usePresetForm(presetData, isEditing, packId || presetData?.packId);

  // Limpar a URL do objeto ao desmontar o componente
  useEffect(() => {
    return () => {
      if (lutPreviewUrl) {
        URL.revokeObjectURL(lutPreviewUrl);
      }
    };
  }, [lutPreviewUrl]);

  // Função para abrir o modal de preview do LUT
  const handleTestLut = () => setLutPreviewOpen(true);

  // Adaptar handleLutChange para gerar URL local para preview
  const handleLutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    // Libera a URL antiga se existir
    if (lutPreviewUrl) {
      URL.revokeObjectURL(lutPreviewUrl);
    }

    if (file) {
      setLutPreviewUrl(URL.createObjectURL(file));
    } else {
      setLutPreviewUrl(null);
    }
    presetForm.handleLutChange(e); // chama o hook original
  };

  // Função de validação
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!presetForm.name.trim()) {
      errors.name = t('nameRequired');
    } else if (presetForm.name.trim().length < 3) {
      errors.name = t('nameMinLength');
    }

    if (!presetForm.description.trim()) {
      errors.description = t('descriptionRequired');
    }

    // if (!presetForm.tags.trim()) {
    //   errors.tags = "As tags são obrigatórias";
    // }


    if (!presetForm.imagePreview) {
      errors.cover = t('coverRequired');
    }

    if (!presetForm.lutFilename) {
      errors.lutFile = t('lutRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Função de salvamento com validação
  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: t('requiredFields'),
        description: t('fixErrors'),
        variant: "destructive",
      });
      return;
    }

    await presetForm.onSubmit();
  };

  // Função para deletar o pack
  const handleDeletePreset = async () => {
    if (!presetId) return;

    try {
      await presetForm.deletePresetMutation.mutateAsync({ id: presetId });
      toast({
        title: t('presetDeleted'),
        description: t('presetDeletedDescription'),
      });
      navigate(-1);
    } catch (error) {
      toast({
        title: t('errorDeletingPreset'),
        description: error instanceof Error ? error.message : t('errorUnknown'),
        variant: "destructive",
      });
    } finally {
      setIsDeleteModalOpen(false);
    }
  };
  // Função para limpar erro de campo específico
  const clearFieldError = (field: string) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Função para limpar erro de categoria
  const clearCategoryError = () => {
    if (validationErrors.category) {
      clearFieldError('category');
    }
  };

  // Mostrar loading enquanto busca os dados do preset
  if (isEditing && isLoadingPreset) {
    return (
      <PageContent title={t('editPreset')} items={[]}>
        <Card className="bg-white/[0.03] border-white/[0.06]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <p className="text-white/60">{t('loadingPreset')}</p>
            </div>
          </CardContent>
        </Card>
      </PageContent>
    );
  }

  return (
    <PageContent title={isEditing ? t('editPreset') : t('newPreset')} items={[
      {
        label: t('home'),
        href: "/",
      },
      {
        label: `${t('packs')} - ${packName || presetData?.pack?.name || t('packs')}`,
        href: `/packs/${packId || presetData?.packId}`,
      },
      {
        label: isEditing ? t('editPreset') : t('newPreset'),
        href: `/packs/${packId}`,
      },
    ]}>
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardContent className="pt-6">
          {/* Alert de validação */}
          {Object.keys(validationErrors).length > 0 && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('validationErrors', { count: Object.keys(validationErrors).length })}
              </AlertDescription>
            </Alert>
          )}

          <form className="space-y-8">
            <div className="space-y-6">
              <PresetBasicInfo
                presetId={presetId}
                isEditing={isEditing}
                name={presetForm.name}
                tags={presetForm.tags}
                setTags={presetForm.setTags}
                description={presetForm.description}
                published={presetForm.published}
                coverPreview={presetForm.imagePreview}
                lutFilename={presetForm.lutFilename}
                lutPreview={lutPreviewUrl || presetForm.lutPreview}
                nameError={!!validationErrors.name}
                tagsError={!!validationErrors.tags}
                coverError={!!validationErrors.cover || presetForm.imageError}
                lutError={!!validationErrors.lutFile || presetForm.lutError}
                descriptionError={!!validationErrors.description}
                isFree={presetForm.isFree}
                handleIsFreeChange={presetForm.handleIsFreeChange}
                handleNameChange={(e) => {
                  presetForm.setName(e.target.value);
                  if (validationErrors.name) clearFieldError('name');
                }}
                handleTagsChange={(e) => {
                  presetForm.setTags(e.target.value);
                  if (validationErrors.tags) clearFieldError('tags');
                }}
                handleDescriptionChange={(e) => {
                  presetForm.setDescription(e.target.value);
                  if (validationErrors.description) clearFieldError('description');
                }}
                handlePublishedChange={presetForm.setPublished}
                handleCoverChange={(e) => {
                  presetForm.handleImageChange(e);
                  if (validationErrors.cover) clearFieldError('cover');
                }}
                handleLutChange={(e) => {
                  handleLutChange(e);
                  if (validationErrors.lutFile) clearFieldError('lutFile');
                }}
                onRemoveCover={() => {
                  presetForm.setImagePreview(null);
                  if (validationErrors.cover) clearFieldError('cover');
                }}
                onRemoveLut={() => {
                  presetForm.setLutFile(null);
                  presetForm.setLutFilename(null);
                  presetForm.setLutUrl(null);
                  setLutPreviewUrl(null);
                  if (validationErrors.lutFile) clearFieldError('lutFile');

                  const lutInput = document.getElementById('lut') as HTMLInputElement;
                  if (lutInput) {
                    lutInput.value = '';
                  }
                }}
                presetsTags={localPresetsTags}
                onPresetsTagsChange={setLocalPresetsTags}
                selectedCategoryIds={presetForm.selectedCategoryIds}
                setSelectedCategoryIds={(categoryIds) => {
                  presetForm.setSelectedCategoryIds(categoryIds);
                  clearCategoryError();
                }}
                categoryError={!!validationErrors.category}
              />
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-white/80">{t('gallery')}</h3>
                <Button
                  type="button"
                  onClick={handleTestLut}
                  className="bg-[#D91657] hover:bg-[#D91657]/90 text-white
                      px-4 py-2 rounded-md shadow-md hover:shadow-lg transition-all duration-200
                      flex items-center gap-2 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!presetForm.lutFilename && !lutPreviewUrl}
                >
                  <span className="relative z-10 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t('viewLutEffect')}
                  </span>
                  <span
                    className="absolute inset-0 bg-white/10 group-hover:bg-white/5 transition-opacity"
                    aria-hidden="true"
                  />
                </Button>
              </div>

              <PresetGallery
                galleryItems={presetForm.galleryItems}
                addGalleryItem={presetForm.addGalleryItem}
                removeGalleryItem={presetForm.removeGalleryItem}
                handleGalleryPhotoChange={(...args) => {
                  presetForm.handleGalleryPhotoChange(...args)
                }}
                isLoading={presetForm.isLoading}
                hasPermissionError={false}
                hasError={false}
              />
            </div>

            <FormFooter
              isLoading={presetForm.isLoading}
              isSuccess={presetForm.createPresetMutation?.isSuccess || presetForm.updatePresetMutation?.isSuccess}
            >
              <div className="flex gap-3 justify-between w-full">
                {isEditing ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={presetForm.createPresetMutation.isPending || presetForm.updatePresetMutation.isPending}
                    className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/70"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('deletePreset')}
                  </Button>
                ):<div/>}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
                    disabled={presetForm.createPresetMutation.isPending || presetForm.updatePresetMutation.isPending}
                  >
                    {t('cancel')}
                  </Button>
                  <SimpleButton
                    onClick={handleSave}
                    isLoading={presetForm.createPresetMutation.isPending || presetForm.updatePresetMutation.isPending}
                    isSuccess={presetForm.createPresetMutation?.isSuccess || presetForm.updatePresetMutation?.isSuccess}
                    loadingText={t('saving')}
                    successText={isEditing ? t('presetUpdated') : t('presetCreated')}
                    successDuration={3000}
                  >
                    {isEditing ? t('updatePreset') : t('createPreset')}
                  </SimpleButton>
                </div>
              </div>

            </FormFooter>

          </form>
        </CardContent>
      </Card>
      <LutPreviewDialog
        isOpen={isLutPreviewOpen}
        onClose={() => setLutPreviewOpen(false)}
        galleryItems={presetForm.galleryItems}
        lutFilename={lutPreviewUrl || presetForm.lutFilename}
      />
      <DeletePresetModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeletePreset}
        name={presetForm.name}
        isLoading={presetForm.deletePresetMutation.isPending}
      />
    </PageContent >
  );
}
